// =============================================================================
// Lade-, Leer- und Fehlerzustaende
// =============================================================================
//
// Grundsatz fuer jeden Text hier: Er richtet sich an eine Lehrkraft, die
// zwischen zwei Stunden auf dem iPad nachsieht - nicht an jemanden, der die
// Anwendung gebaut hat.
//
// Daraus folgt:
//   - Kein HTTP-Status, kein "fetch failed", kein CORS im sichtbaren Text.
//   - Sagen, was los ist, und was die Person jetzt tun kann.
//   - Wenn sie selbst nichts tun kann, das auch sagen - statt sie eine
//     Fehlersuche versuchen zu lassen, die ihr nicht zusteht.
//
// Die technische Ursache landet in der Konsole und im aufklappbaren Bereich
// am Ende. Sie ist fuer den Fall gedacht, dass jemand nachfragt.
// =============================================================================

import { AlertTriangle, Inbox, Loader2, PlugZap, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { Karte } from './Karte';
import type { FehlerArt } from '../directus/client';

export function LadeAnzeige({ was = 'Inhalte' }: { was?: string }) {
  return (
    <div
      className="flex items-center justify-center gap-3 py-16 text-sm text-marke-grau"
      role="status"
      aria-live="polite"
    >
      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
      {was} werden geladen …
    </div>
  );
}

/** Grundgeruest fuer jede Meldung: Symbol, Ueberschrift, Erklaerung, Aktion. */
function Meldung({
  symbol,
  ueberschrift,
  children,
  aktion,
  ton = 'ruhig',
}: {
  symbol: ReactNode;
  ueberschrift: string;
  children: ReactNode;
  aktion?: ReactNode;
  ton?: 'ruhig' | 'warnung';
}) {
  const farbe = ton === 'warnung' ? 'text-ampel-kritisch' : 'text-marke-grau';
  return (
    <Karte className="px-6 py-10 text-center">
      <div className={`mb-3 flex justify-center ${farbe}`} aria-hidden="true">
        {symbol}
      </div>
      <h2 className="text-base font-semibold text-marke-tinte">{ueberschrift}</h2>
      <div className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-marke-grau">
        {children}
      </div>
      {aktion !== undefined && <div className="mt-5 flex justify-center">{aktion}</div>}
    </Karte>
  );
}

function Schaltflaeche({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-lg bg-marke-blau px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
    >
      {children}
    </button>
  );
}

/** Zustand 1 aus dem Startprompt: Directus antwortet nicht. */
export function VerbindungFehlt({
  art,
  technisch,
  erneutLaden,
}: {
  art: FehlerArt;
  technisch: string;
  erneutLaden: () => void;
}) {
  // Drei Ursachen, drei Texte. Alle drei sagen der Lehrkraft dasselbe:
  // Es liegt nicht an dir, und du kannst es nicht reparieren.
  const texte: Record<FehlerArt, { ueberschrift: string; text: ReactNode }> = {
    'keine-verbindung': {
      ueberschrift: 'Die Inhalte sind gerade nicht erreichbar',
      text: (
        <>
          Die App konnte die Termine und Texte nicht laden. Meist liegt das an der
          Internetverbindung oder daran, dass der Schulserver kurz nicht antwortet.
          <br />
          <br />
          Versuche es in ein paar Minuten noch einmal. Bleibt es dabei, gib der Stufenleitung
          Bescheid.
        </>
      ),
    },
    'kein-zugriff': {
      ueberschrift: 'Die App darf die Inhalte gerade nicht lesen',
      text: (
        <>
          Der Server hat geantwortet, gibt die Inhalte aber nicht heraus. Das ist eine
          Einstellungssache und nichts, was sich hier beheben lässt.
          <br />
          <br />
          Bitte gib der Stufenleitung Bescheid — an deinem Gerät liegt es nicht.
        </>
      ),
    },
    'antwort-unbrauchbar': {
      ueberschrift: 'Die Inhalte kamen unvollständig an',
      text: (
        <>
          Die App hat eine Antwort bekommen, konnte sie aber nicht verwenden.
          <br />
          <br />
          Versuche es noch einmal. Bleibt es dabei, gib der Stufenleitung Bescheid.
        </>
      ),
    },
  };

  const { ueberschrift, text } = texte[art];

  return (
    <Meldung
      symbol={<PlugZap size={28} />}
      ueberschrift={ueberschrift}
      ton="warnung"
      aktion={
        <Schaltflaeche onClick={erneutLaden}>
          <RefreshCw size={15} aria-hidden="true" />
          Erneut versuchen
        </Schaltflaeche>
      }
    >
      {text}
      <TechnischerHinweis technisch={technisch} />
    </Meldung>
  );
}

/** Zustand 2: Die Collection ist leer - es ist schlicht noch nichts gepflegt. */
export function NochNichtsGepflegt({
  was,
  hinweis,
}: {
  was: string;
  hinweis?: string;
}) {
  return (
    <Meldung symbol={<Inbox size={28} />} ueberschrift={`Hier steht noch nichts`}>
      {was} wurde bisher nichts eingetragen.
      {hinweis !== undefined && (
        <>
          <br />
          <br />
          {hinweis}
        </>
      )}
    </Meldung>
  );
}

/** Sonderfall: Die App wurde ohne Adresse ausgeliefert. Das kann nur beim
 *  Einrichten passieren, deshalb darf dieser Text technischer sein - er
 *  richtet sich an die Person, die den Server aufgesetzt hat. */
export function KonfigurationFehlt({ grund }: { grund: string }) {
  return (
    <div className="mx-auto max-w-2xl px-5 py-16">
      <Meldung
        symbol={<AlertTriangle size={28} />}
        ueberschrift="Die App ist noch nicht fertig eingerichtet"
        ton="warnung"
      >
        {grund}
        <br />
        <br />
        Ohne sie weiß die App nicht, wo sie die Inhalte holen soll. Einzutragen ist sie in der{' '}
        <code className="rounded bg-marke-flaeche px-1 py-0.5 text-xs">.env</code> als{' '}
        <code className="rounded bg-marke-flaeche px-1 py-0.5 text-xs">APP_DIRECTUS_URL</code>,
        lokal als <code className="rounded bg-marke-flaeche px-1 py-0.5 text-xs">VITE_DIRECTUS_URL</code>{' '}
        in <code className="rounded bg-marke-flaeche px-1 py-0.5 text-xs">frontend/.env.local</code>.
      </Meldung>
    </div>
  );
}

/** Die technische Ursache, zugeklappt. Wer sie braucht, findet sie; wer sie
 *  nicht braucht, sieht nur ein unauffaelliges Woertchen. */
function TechnischerHinweis({ technisch }: { technisch: string }) {
  if (technisch.trim() === '') return null;
  return (
    <details className="mt-5 text-left">
      <summary className="cursor-pointer text-xs text-marke-grau">
        Technische Angabe für die Fehlersuche
      </summary>
      <p className="mt-2 rounded-lg bg-marke-flaeche px-3 py-2 font-mono text-xs break-all text-marke-grau">
        {technisch}
      </p>
    </details>
  );
}
